import React, { useState, useRef } from 'react';
import Form from 'global/components/form';
import { get, set, unset, size, first } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { Button } from '../../../global/components/Button';
import { Row } from '../../../global/components/row';
import { Icon } from '@mui/material';
import SnackAlerts from 'global/components/snack-alerts';
import { stringObj } from '../../../global/utils';
import { sendData } from 'redux/actions/sendData';
import { awaitAll } from '../../../global/utils/awaitAll';
import { UploadFiles } from '../../../redux/actions/imageUploader';
import { sortItemsAlphabetically } from 'global/utils';
import { ADMIN_FLEX_COLUMN, AdminContentRoot } from './styles';
import { useLocalId } from '../../../global/hooks/useLocalId';

export const CreateSBS = (props) => {
  const { doc } = props;
  const nextLocalId = useLocalId();

  const [state, setState] = useState(() => ({
    isEdit: false,
    steps: [nextLocalId()],
    update: 1,
  }));

  const [formState, setFormState] = useState({});
  const [alerts, setAlerts] = useState();

  const cpref = useRef();

  const supersets = useRetrieveData(
    { url: '/systems' },
    {
      build: ['systems'],
      transformData: (d) => {
        return d.map((item) => ({ ...item, value: item.id, title: item.name }));
      },
    }
  );

  const classifications = useRetrieveData(
    { url: '/classifications' },
    {
      build: ['classifications'],
      transformData: (d) => {
        return d.map((item) => ({ ...item, value: item.id, title: item.name }));
      },
    }
  );

  const setCpFormState = (cp, fs) => {
    const cps = get(cpref, 'current', {});
    set(cps, cp, fs);
    set(cpref, 'current', cps);
    setState((s) => ({ ...s, update: s + 1 }));
  };

  const data = first(doc) || doc;

  const submit = async () => {
    try {
      const cl = { ...formState };

      unset(cl, 'system');

      const createdSbs = await sendData(
        {
          url: '/step-by-steps',
          method: 'post',
          data: {
            data: {
              type: 'step-by-steps',
              attributes: stringObj(cl),
            },
          },
        },
        {
          normalize: { build: ['stepBySteps'] },
        }
      );

      const clid = get(createdSbs, '0.id', '').toString();

      const cps = [];
      state.steps.map((step, i) => {
        const val = get(cpref, `current.${step}`);

        const dd = {
          data: {
            type: 'step-by-step-items',
            attributes: stringObj({
              name: `Step ${i + 1}`,
              step_by_step_id: clid,
              description: get(val, 'description'),
              order: i,
              images: val.images,
            }),
          },
        };

        cps.push({ data: dd });
      });

      await awaitAll(
        cps.map((cp) => () => async () => {
          await UploadFiles(cp.data);

          const result = await sendData(
            {
              url: '/step-by-step-items',
              method: 'post',
              data: cp.data,
            },
            { normalize: { build: ['stepBySteps'] } }
          );

          if (cp.onSuccess) {
            await cp.onSuccess(result);
          }
        })
      );

      const amt = size(get(data, 'documents'));

      await sendData({
        url: `/edition-documents`,
        method: 'post',
        data: {
          data: {
            type: 'edition-documents',
            attributes: stringObj({
              edition_id: get(data, 'editionId', get(data, 'id')),
              documentable_id: clid,
              documentable_type: 'step_by_step',
              classification_id: get(cl, 'classification_id'),
              order: amt,
              primary_document: !amt ? 1 : 0,
            }),
          },
        },
      });

      props.onClose && props.onClose(true);
    } catch (err) {
      setAlerts({ message: err.message, severity: 'error' });
    }
  };

  const addStep = () => {
    const id = nextLocalId();
    setState((s) => ({ ...s, steps: [...s.steps, id] }));
  };

  const removeStep = (step) => {
    const steps = state.steps.filter((c) => c !== step);
    setState((s) => ({ ...s, steps }));
    unset(cpref, `current.${step}`);
  };

  const onSort = (order) => {
    setState((s) => ({ ...s, steps: order.map((o) => o.key) }));
  };

  const allStepsHaveDescription = () => {
    const descripts = state.steps.map((m) => get(cpref, `current.${m}.description`));
    const valid = descripts.filter((f) => !!f);
    return size(valid) >= size(descripts);
  };

  const createDisabled = !get(formState, 'classification_id') || !get(formState, 'name') || !allStepsHaveDescription();

  const hasClassification = !!get(data, 'classification');

  return (
    <AdminContentRoot>
      <Form
        showAllScreens
        formLoading={state.formLoading}
        onFormState={setFormState}
        formStyle={ADMIN_FLEX_COLUMN}
        screens={[
          {
            inputs: [
              {
                id: 'name',
                type: 'TextField',
                defaultValue: () => '',
                props: {
                  label: 'Title',
                },
              },
              {
                id: 'description',
                type: 'TextField',
                defaultValue: () => get(data, 'description', ''),
                props: {
                  label: 'Description',
                  rows: 6,
                  multiline: true,
                },
              },
              {
                id: 'system',
                type: 'Select',
                data: () => supersets.data,
                defaultValue: () => get(data, 'classification.system.id', 0),
                props: {
                  label: 'Category',
                  placeholder: 'Choose a Category',
                  disabled: !!hasClassification,
                },
              },
              {
                id: 'classification_id',
                type: 'Select',
                data: () => sortItemsAlphabetically(classifications.data.filter((f) => get(f, 'system.id') == get(formState, 'system'))),
                defaultValue: () => get(data, 'classification.id', 0),
                props: {
                  label: 'Classification',
                  placeholder: 'Choose Classification',
                  disabled: !get(formState, 'system') || !!hasClassification,
                  helperText: 'Why do we require a classification? This is the standardized data-point that organizes content together and allows the site to collate data more effectively.',
                },
              },
            ],
          },
          {
            sortable: true,
            onSort,
            screenContainer: {
              props: {
                style: {
                  marginBottom: 20,
                },
              },
            },
            inputs: state.steps.map((step, i) => ({
              id: step,
              add: i === state.steps.length - 1,
              remove: true,
              onAdd: addStep,
              style: { padding: 0 },
              onRemove: () => {
                removeStep(step);
              },
              component: (
                <Form
                  showAllScreens
                  onFormState={(fs) => setCpFormState(step, fs)}
                  screens={[
                    {
                      title: `Step ${i + 1}`,
                      inputs: [
                        {
                          id: 'description',
                          type: 'TextField',
                          defaultValue: () => get(cpref, `current.${step}.description`, ''),
                          props: {
                            multiline: true,
                            rows: 5,
                            label: 'Description',
                            placeholder: 'Step Description',
                          },
                        },
                        {
                          id: 'images',
                          type: 'DropArea',
                          defaultValue: () => get(cpref, `current.${step}.images`, []),
                        },
                      ],
                    },
                  ]}
                />
              ),
            })),
          },
        ]}
      />
      <Row fullWidth center>
        <Button disabled={createDisabled} onClick={submit} startIcon={<Icon>save</Icon>}>
          CREATE
        </Button>
      </Row>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </AdminContentRoot>
  );
};
